import { PageHeader } from '../../components/layout/AppLayout'
import { Badge, Card, CardBody, CardHeader, EmptyState } from '../../components/ui/Card'
import { ButtonLink } from '../../components/ui/Button'

interface Stat {
  label: string
  value: string
  hint: string
}

// Placeholder figures until the campaign endpoints exist. Kept obviously empty rather
// than filled with plausible fake numbers, so nobody mistakes this for live data.
const stats: Stat[] = [
  { label: 'Active subscriptions', value: '0', hint: 'Paid and scheduled for service' },
  { label: 'Awaiting payment', value: '0', hint: 'Invoiced, not yet cleared' },
  { label: 'Scouts on routes', value: '0', hint: 'Assigned at least one address' },
  { label: 'Open messages', value: '0', hint: 'Customer threads needing a reply' },
]

export function Overview() {
  return (
    <>
      <PageHeader
        title="Overview"
        description="Where this season stands."
        action={<Badge tone="warning">Setup incomplete</Badge>}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardBody>
              <p className="text-sm text-[color:var(--text-muted)]">{stat.label}</p>
              <p className="mt-1.5 text-3xl font-semibold tabular-nums">{stat.value}</p>
              <p className="mt-1 text-xs text-[color:var(--text-muted)]">{stat.hint}</p>
            </CardBody>
          </Card>
        ))}
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Finish setting up"
            description="A customer cannot sign up until these are done."
          />
          <CardBody className="space-y-3">
            <SetupStep
              label="Draw a service area"
              detail="Signups are validated against it."
              to="/app/service-areas"
            />
            <SetupStep
              label="Confirm your holiday schedule"
              detail="Starts from the standard set."
              to="/app/holidays"
            />
            <SetupStep label="Open a campaign" detail="Sets the price for the season." to="/app/subscriptions" />
            <SetupStep label="Invite your Scouts" detail="They see only their own routes." to="/app/scouts" />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Next holiday" />
          <EmptyState
            title="No campaign open"
            description="Once a campaign is open, the next holiday and its placement and pickup dates show up here."
          />
        </Card>
      </div>
    </>
  )
}

function SetupStep({ label, detail, to }: { label: string; detail: string; to: string }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-[color:var(--surface-border)] px-4 py-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{label}</p>
        <p className="truncate text-xs text-[color:var(--text-muted)]">{detail}</p>
      </div>
      <ButtonLink to={to} variant="secondary" size="sm">
        Open
      </ButtonLink>
    </div>
  )
}
